import { useMemo } from 'react';

import { IEmployee } from 'models';
import { useFetchEmployeesQuery, useFetchEmployeeQuery, useUpdateEmployeeMutation } from 'store/apis';
import { selectCurrentUserInfo } from 'store/slices/auth.slice';
import { useAppSelector } from 'store/store.hook';
import { apiVersion, baseUrl } from 'store/apis/apis.const';

export const useEmployees = (): [boolean, IEmployee[]] => {
  const { orgSlug } = useAppSelector(selectCurrentUserInfo);

  const { data, isLoading } = useFetchEmployeesQuery({ orgSlug });

  return useMemo(() => [isLoading, data], [isLoading, data || []]);
};

type UseEmployeesTableRowsProps = {
  searchKeyword: string;
  setState?: (state: any) => void;
};

export const useEmployee = ({ employeeId: id }: { employeeId: number }): [boolean, IEmployee] => {
  const { orgSlug } = useAppSelector(selectCurrentUserInfo);

  const { data, isLoading } = useFetchEmployeeQuery({ orgSlug, id });

  return useMemo(() => [isLoading, data], [isLoading, data]);
};

export const useEmployeesTableRows = ({
  searchKeyword,
  setState = () => {},
}: UseEmployeesTableRowsProps): [boolean, IEmployee[]] => {
  const [loading, employeeList] = useEmployees();
  const { orgSlug } = useAppSelector(selectCurrentUserInfo);
  const [updateEmployee] = useUpdateEmployeeMutation();

  /**
   * Init employees
   */
  let employees = [...(employeeList || [])];

  /**
   * Filter employees by keyword
   */
  const keyword = searchKeyword.trim().toLocaleLowerCase();

  if (keyword.length > 0) {
    employees = employees.filter((item) => {
      if (`${item.id}`.includes(keyword)) {
        return true;
      }

      if (item.name.toLowerCase().includes(keyword)) {
        return true;
      }

      if (item.email.toLowerCase().includes(keyword)) {
        return true;
      }

      if (item.code && item.code.toLowerCase().includes(keyword)) {
        return true;
      }

      if (item.phone && item.phone.includes(keyword)) {
        return true;
      }

      return false;
    });
  }

  /**
   * Add actions
   */
  const editEmployee = (employee: IEmployee) =>
    setState((prev: any) => ({ ...prev, employeeId: employee.id, visibleEmployeeUpdateDialog: true }));

  const activateEmployee = (employee: IEmployee) => {
    const { id, ...rest } = employee;

    fetch(`${baseUrl}/${orgSlug}/${apiVersion}/employee/code`, {
      method: 'GET',
      credentials: 'include',
    })
      .then((res) => res.json())
      .then(({ payload: { code } }) => {
        if (code) {
          updateEmployee({ orgSlug, id, employee: { ...rest, code } });
        }
      });
  };

  const deactivateEmployee = (employee: IEmployee) => {
    const { id, ...rest } = employee;
    updateEmployee({ orgSlug, id, employee: { ...rest, code: null } });
  };

  const userBuildingAssignments = (employee: IEmployee) =>
    setState((prev: any) => ({ ...prev, employeeId: employee.id, visibleUserAssignedBuildingsDialog: true }));

  employees = employees.map((item) => ({
    ...item,
    editEmployee,
    activateEmployee,
    deactivateEmployee,
    userBuildingAssignments,
  }));

  return [loading, employees];
};
